import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { storiesApi } from '../api/stories';
import { outlineChapters, outlineScenes, peopleForPov, peopleForVoice, styleCardsApi } from '../api/styleCards';
import { NarrativePovForm } from '../components/NarrativePov/NarrativePovSettle';
import { useSyncStoryContext } from '../hooks/useSyncStoryContext';
import PersonControl from '../components/Common/PersonControl';
import ConfirmModal from '../components/Common/ConfirmModal';
import StoryPicker from '../components/Common/StoryPicker';
import { usePaidConfirm } from '../components/Common/usePaidConfirm';
import { useUnsavedChangesWarning } from '../hooks/useUnsavedChangesWarning';
import { sectionPersonBody } from '../utils/personMode';
import { notifySceneVoicePointed } from '../utils/sceneVoicePointed';
import { povSourceLabel, styleCardsPath } from '../utils/styleCardsPath';
import { styleRoomEnabled } from '../utils/styleRoom';
import StyleRoom from './StyleRoom';
import {
  GENERATE_VOICE_FROM_CHARACTER_NOTE,
  GENERATE_VOICE_FROM_THEIR_CHARACTER_NOTE,
  GENERATE_VOICE_HINT,
  LOAD_STYLE_NOTES_ERROR,
  LOADING_STYLE_NOTES,
  NO_DEFAULT_VOICE_YET,
  NO_VOICE_SAVED_YET,
  NO_VOICES_IN_LIBRARY,
  NONE_BOOK_VOICE,
  OPEN_BOOK_FOR_STYLE_NOTES,
  PARENT_VOICE_LINE,
  PERSON_FOR_SCENE,
  POINT_CHAPTER_HINT,
  POINT_SCENE_AT_VOICE,
  REVIEW_THIS_VOICE,
  SAVED,
  SAVED_VOICES,
  SELECT_BOOK_FOR_STYLE_NOTE,
  STYLE_ACTION_FAILED,
  STYLE_LEDE,
  STYLE_NOTES,
  THIN_CHARACTER_NOTE,
  TRY_AGAIN,
  DISCARD_UNSAVED,
  KEEP_EDITING,
  UNSAVED_CHANGES_WARNING,
  UNSAVED_CLOSE_CHOICE,
  UNTITLED_VOICE,
  VOICE_INSTRUCTION_PLACEHOLDER,
  VOICE_NAME,
  VOICE_SAVED_FOR_BOOK,
  VOICE_TIED_TO_PERSON,
  COPY_BOOK_VOICE_INTO_SCENE,
  EXTRA_GUIDANCE_HINT,
} from '../utils/writerCopy';
import './StyleCardsPage.css';

const unwrapList = (res) => (Array.isArray(res) ? res : res?.data || []);

const byRecency = (a, b) => {
  const at = new Date(a.last_activity_at || a.created_at).getTime();
  const bt = new Date(b.last_activity_at || b.created_at).getTime();
  return bt - at;
};

export const NOTES_AUTOSAVE_MS = 1500;

const NOTE_KEYS = ['exemplars', 'mechanics', 'lexicon', 'tone', 'direction_note'];

const NOTE_FIELD_IDS = {
  'sc-exemplars': 'exemplars',
  'sc-mechanics': 'mechanics',
  'sc-lexicon': 'lexicon',
  'sc-tone': 'tone',
  'sc-direction': 'direction_note',
};

const emptyNotes = () => ({
  exemplars: '',
  mechanics: '',
  lexicon: '',
  tone: '',
  direction_note: '',
});

const notesFromCard = (card) => ({
  exemplars: card?.exemplars || '',
  mechanics: card?.mechanics || '',
  lexicon: card?.lexicon || '',
  tone: card?.tone || '',
  direction_note: card?.direction_note || '',
});

const sameNotes = (left, right) => NOTE_KEYS.every(
  (key) => (left?.[key] || '') === (right?.[key] || ''),
);

/** Boxes the writer can still be typing in. The save response re-renders the
 *  page, and a controlled value snaps back to React state, so read the
 *  fields themselves before that render. */
function readMountedNotes(live) {
  const next = { ...emptyNotes(), ...live };
  if (typeof document === 'undefined') return next;
  Object.entries(NOTE_FIELD_IDS).forEach(([id, key]) => {
    const node = document.getElementById(id);
    if (!node || typeof node.value !== 'string') return;
    next[key] = node.value;
  });
  return next;
}

/** Server text stays out of a box the writer changed after that save was sent. */
function notesAfterEcho(serverNotes, live, saved, sent) {
  const next = { ...emptyNotes() };
  NOTE_KEYS.forEach((key) => {
    const liveValue = live?.[key] || '';
    const savedValue = saved?.[key] || '';
    if (sent && liveValue !== (sent[key] || '')) next[key] = liveValue;
    else if (liveValue !== savedValue) next[key] = liveValue;
    else next[key] = serverNotes?.[key] || '';
  });
  return next;
}

const emptyScaffold = () => ({
  name: '',
  exemplars: '',
  mechanics: '',
  lexicon: '',
  tone: '',
  direction_note: '',
  character_entity_id: '',
});

function statusLabel(card) {
  if (!card) return '';
  if (card.is_book_default) return 'Book default';
  if (card.in_library) return 'Library';
  if (card.status === 'rejected') return 'Rejected';
  if (card.status === 'draft') return 'Draft';
  return card.status;
}

/**
 * Style Notes room (#855). One voice = one resolved instruction.
 * Draft / reject / direction-note / save-to-library / promote.
 * POV is first-order. Chapter override and scene pointer are wired here.
 * Not Story Rules. Not the scene-writing page. Not whose-head.
 */
export function StyleNotesPage() {
  const { storyId: routeStoryId } = useParams();
  const { currentProject, currentStory, setProjectAndStory, storyListVersion } = useApp();
  const navigate = useNavigate();

  const openedId = routeStoryId || currentStory?.id || null;
  const { storyId, loadedStory } = useSyncStoryContext({ routeStoryId: openedId });

  const [pickerOpen, setPickerOpen] = useState(false);
  const [projectStories, setProjectStories] = useState(null);
  const [payload, setPayload] = useState(null);
  const [outline, setOutline] = useState({ sections: [], chapters: [], scenes: [], characters: [] });
  const [effective, setEffective] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [scaffold, setScaffold] = useState(emptyScaffold);
  const [resolvedEdit, setResolvedEdit] = useState('');
  const [chapterId, setChapterId] = useState('');
  const [sceneId, setSceneId] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const { ask: askPaid, dialog: paidDialog } = usePaidConfirm();
  const [error, setError] = useState(null);
  const [saveNote, setSaveNote] = useState('');
  const [pointNote, setPointNote] = useState('');
  const [voicePersonId, setVoicePersonId] = useState('');
  const [pendingGenerate, setPendingGenerate] = useState(null);
  const [savedScaffold, setSavedScaffold] = useState({
    exemplars: '',
    mechanics: '',
    lexicon: '',
    tone: '',
  });
  const [appliedVoiceId, setAppliedVoiceId] = useState(undefined);
  const [povDirty, setPovDirty] = useState(false);
  const [povEpoch, setPovEpoch] = useState(0);
  const [voiceSwitchOpen, setVoiceSwitchOpen] = useState(false);
  const onPovDirty = useCallback((next) => setPovDirty(next), []);
  const loadAbortRef = useRef(null);
  const voiceSwitchRef = useRef(null);
  const formDirtyRef = useRef(false);
  const notesTimerRef = useRef(0);
  const notesChainRef = useRef(Promise.resolve(null));
  const notesCardIdRef = useRef(null);
  const notesFlightRef = useRef(null);
  const notesQueuedRef = useRef(false);
  const notesEpochRef = useRef(0);
  const liveNotesRef = useRef(emptyNotes());
  const savedNotesRef = useRef(emptyNotes());
  const pendingEchoRef = useRef(null);
  const loadedVoiceIdRef = useRef(undefined);
  const guidanceRef = useRef({ dirty: false });
  const aliveRef = useRef(true);
  const [notesSaved, setNotesSaved] = useState(false);

  const activeProjectId = currentProject?.id || loadedStory?.project_id || null;

  useEffect(() => {
    if (!activeProjectId) { setProjectStories(null); return undefined; }
    let cancelled = false;
    storiesApi.list(activeProjectId)
      .then((res) => { if (!cancelled) setProjectStories(unwrapList(res)); })
      .catch(() => { if (!cancelled) setProjectStories([]); });
    return () => { cancelled = true; };
  }, [activeProjectId, storyListVersion]);

  useEffect(() => {
    if (routeStoryId) { setPickerOpen(false); return; }
    if (currentStory?.id) {
      setPickerOpen(false);
      navigate(styleCardsPath(currentStory.id), { replace: true });
      return;
    }
    if (!currentProject) { setPickerOpen(true); return; }
    if (!projectStories || projectStories.length === 0) return;
    const mostRecent = [...projectStories].sort(byRecency)[0];
    setProjectAndStory(currentProject, mostRecent);
    navigate(styleCardsPath(mostRecent.id), { replace: true });
  }, [routeStoryId, currentStory?.id, currentProject, projectStories, navigate, setProjectAndStory]);

  const load = useCallback(async (preferId) => {
    if (!storyId) return;
    loadAbortRef.current?.abort();
    const controller = new AbortController();
    loadAbortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const list = await styleCardsApi.list(storyId, { signal: controller.signal });
      if (controller.signal.aborted) return;
      setPayload(list);
      const cards = list?.cards || [];
      const defaultId = list?.book_default_card_id;
      const preferred = preferId ? cards.find((c) => c.id === preferId) : null;
      const first = cards.find((c) => c.id === defaultId) || cards[0];
      setSelectedId((prev) => {
        if (preferId === null) return null;
        if (preferred) return preferred.id;
        if (prev && cards.some((c) => c.id === prev)) return prev;
        return first?.id || null;
      });
    } catch (err) {
      if (err?.name === 'AbortError') return;
      setError(err?.message || LOAD_STYLE_NOTES_ERROR);
    } finally {
      if (loadAbortRef.current === controller) setLoading(false);
    }
    if (controller.signal.aborted) return;
    try {
      const pointers = await styleCardsApi.outline(storyId, { signal: controller.signal });
      if (!controller.signal.aborted) {
        setOutline(pointers || { sections: [], chapters: [], scenes: [], characters: [] });
      }
    } catch (err) {
      if (err?.name === 'AbortError') return;
      setError(err?.message || 'Could not load this book\'s chapters and scenes.');
    }
  }, [storyId]);

  useEffect(() => {
    load();
    return () => { loadAbortRef.current?.abort(); };
  }, [load]);

  const selected = useMemo(
    () => (payload?.cards || []).find((c) => c.id === selectedId) || null,
    [payload, selectedId],
  );

  useEffect(() => () => { aliveRef.current = false; }, []);

  useEffect(() => {
    const serverNotes = notesFromCard(selected);
    const nextId = selected?.id ?? null;
    setAppliedVoiceId(nextId);
    if (nextId === loadedVoiceIdRef.current) {
      // Same voice: a save echo must not move the baseline backward.
      pendingEchoRef.current = null;
      return;
    }
    const previousId = loadedVoiceIdRef.current;
    loadedVoiceIdRef.current = nextId;
    const createdFromEmpty = previousId == null
      && nextId != null
      && notesCardIdRef.current === nextId;
    if (!createdFromEmpty) setNotesSaved(false);
    const switchingVoice = previousId != null && nextId != null && previousId !== nextId;
    const sentEcho = pendingEchoRef.current;
    const liveAheadOfSend = Boolean(sentEcho)
      && !sameNotes(liveNotesRef.current, sentEcho);
    if (!selected) {
      const nextNotes = notesAfterEcho(
        emptyNotes(),
        liveNotesRef.current,
        savedNotesRef.current,
        pendingEchoRef.current,
      );
      pendingEchoRef.current = null;
      liveNotesRef.current = nextNotes;
      setResolvedEdit('');
      setScaffold((prev) => ({
        ...emptyScaffold(),
        ...nextNotes,
        name: prev.name,
        character_entity_id: prev.character_entity_id,
      }));
      setSavedScaffold((prevSaved) => (
        serverNotes.exemplars === '' && serverNotes.mechanics === ''
          && serverNotes.lexicon === '' && serverNotes.tone === ''
          ? {
            exemplars: '',
            mechanics: '',
            lexicon: '',
            tone: '',
          }
          : prevSaved
      ));
      return;
    }
    if ((switchingVoice || !createdFromEmpty) && !liveAheadOfSend) {
      pendingEchoRef.current = null;
      liveNotesRef.current = serverNotes;
      savedNotesRef.current = serverNotes;
      setResolvedEdit(selected.resolved_text || '');
      setScaffold({
        name: selected.name || '',
        character_entity_id: selected.character_entity_id || '',
        ...serverNotes,
      });
      setSavedScaffold({
        exemplars: serverNotes.exemplars,
        mechanics: serverNotes.mechanics,
        lexicon: serverNotes.lexicon,
        tone: serverNotes.tone,
      });
      return;
    }
    const sent = pendingEchoRef.current;
    const nextNotes = notesAfterEcho(
      serverNotes,
      liveNotesRef.current,
      savedNotesRef.current,
      sent,
    );
    pendingEchoRef.current = null;
    liveNotesRef.current = nextNotes;
    if (!sent || sameNotes(nextNotes, sent)) {
      savedNotesRef.current = serverNotes;
      setSavedScaffold({
        exemplars: serverNotes.exemplars,
        mechanics: serverNotes.mechanics,
        lexicon: serverNotes.lexicon,
        tone: serverNotes.tone,
      });
    }
    setResolvedEdit((prev) => prev || selected.resolved_text || '');
    setScaffold((prev) => ({
      name: prev.name || selected.name || '',
      character_entity_id: prev.character_entity_id || selected.character_entity_id || '',
      ...nextNotes,
    }));
  }, [
    selected?.id,
    selected?.resolved_text,
    selected?.name,
    selected?.direction_note,
    selected?.character_entity_id,
    selected?.exemplars,
    selected?.mechanics,
    selected?.lexicon,
    selected?.tone,
  ]);

  useEffect(() => {
    notesCardIdRef.current = selectedId || null;
  }, [selectedId]);

  const refreshEffective = useCallback(async () => {
    if (!storyId) return;
    try {
      const next = await styleCardsApi.effective(storyId, {
        chapterId: chapterId || undefined,
        sceneId: sceneId || undefined,
      });
      setEffective(next);
    } catch (err) {
      setError(err?.message || 'Could not resolve inheritance.');
    }
  }, [storyId, chapterId, sceneId]);

  useEffect(() => { refreshEffective(); }, [refreshEffective]);

  const run = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      return await fn();
    } catch (err) {
      setError(err?.message || STYLE_ACTION_FAILED);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const abandonNotesFlight = () => {
    notesEpochRef.current += 1;
    notesQueuedRef.current = false;
    window.clearTimeout(notesTimerRef.current);
  };

  const rememberNotes = (notes) => {
    const next = {
      exemplars: notes?.exemplars || '',
      mechanics: notes?.mechanics || '',
      lexicon: notes?.lexicon || '',
      tone: notes?.tone || '',
      direction_note: notes?.direction_note || '',
    };
    liveNotesRef.current = next;
    savedNotesRef.current = next;
    return next;
  };

  const revertDraftNotes = () => {
    abandonNotesFlight();
    setPovDirty(false);
    setPovEpoch((epoch) => epoch + 1);
    const next = {
      ...liveNotesRef.current,
      exemplars: savedScaffold.exemplars,
      mechanics: savedScaffold.mechanics,
      lexicon: savedScaffold.lexicon,
      tone: savedScaffold.tone,
    };
    liveNotesRef.current = next;
    setScaffold((prev) => ({
      ...prev,
      exemplars: next.exemplars,
      mechanics: next.mechanics,
      lexicon: next.lexicon,
      tone: next.tone,
    }));
  };

  const onGenerateFromCard = async () => {
    const ok = await askPaid('style_voice');
    if (!ok) return;
    const person = peopleForVoice(outline).find((p) => p.pickId === voicePersonId);
    if (!person) {
      setError('Pick a person with a confirmed Character note.');
      return;
    }
    const card = await run(() => styleCardsApi.generateFromCard(storyId, {
      bible_entry_id: person.bible_entry_id || null,
      character_entity_id: person.character_entity_id || null,
    }));
    if (card) {
      revertDraftNotes();
      setSelectedId(card.id);
      setResolvedEdit(card.resolved_text || '');
      setScaffold((s) => ({
        ...s,
        name: card.name || s.name,
        character_entity_id: card.character_entity_id || person.character_entity_id || '',
      }));
      setPendingGenerate({
        id: card.id,
        thin: Boolean(card.thin),
        name: card.name,
      });
      setSaveNote('');
      await load(card.id);
    }
  };

  const notesFromScaffold = () => ({
    exemplars: scaffold.exemplars,
    mechanics: scaffold.mechanics,
    lexicon: scaffold.lexicon,
    tone: scaffold.tone,
  });

  const persistNotes = useCallback(() => {
    if (!guidanceRef.current.storyId) return Promise.resolve(null);
    notesQueuedRef.current = true;
    if (notesFlightRef.current) return notesChainRef.current;

    const job = (async () => {
      let last = null;
      while (notesQueuedRef.current && aliveRef.current) {
        notesQueuedRef.current = false;
        const story = guidanceRef.current.storyId;
        const epoch = notesEpochRef.current;
        const sent = readMountedNotes(liveNotesRef.current);
        liveNotesRef.current = sent;
        if (!story || sameNotes(sent, savedNotesRef.current)) continue;
        const cardId = notesCardIdRef.current || null;
        try {
          last = await styleCardsApi.saveNotes(story, { card_id: cardId, ...sent });
        } catch (err) {
          if (aliveRef.current && epoch === notesEpochRef.current) {
            setError(err?.message || STYLE_ACTION_FAILED);
          }
          continue;
        }
        if (!aliveRef.current || epoch !== notesEpochRef.current) {
          notesQueuedRef.current = false;
          break;
        }
        if (!last) continue;
        const liveNow = readMountedNotes(liveNotesRef.current);
        liveNotesRef.current = liveNow;
        notesCardIdRef.current = last.id;
        pendingEchoRef.current = sent;
        savedNotesRef.current = { ...sent };
        if (sameNotes(liveNow, sent)) {
          setSavedScaffold({
            exemplars: sent.exemplars,
            mechanics: sent.mechanics,
            lexicon: sent.lexicon,
            tone: sent.tone,
          });
          setNotesSaved(true);
        } else {
          notesQueuedRef.current = true;
          setScaffold((prev) => (
            sameNotes(prev, liveNow) ? prev : { ...prev, ...liveNow }
          ));
        }
        setSelectedId(last.id);
        setPayload((prev) => {
          const cards = prev?.cards ? [...prev.cards] : [];
          const idx = cards.findIndex((item) => item.id === last.id);
          if (idx === -1) cards.unshift(last);
          else cards[idx] = { ...cards[idx], ...last };
          return {
            cards,
            book_default_card_id: prev?.book_default_card_id ?? null,
            book_pov: prev?.book_pov ?? null,
          };
        });
      }
      return last;
    })();

    notesFlightRef.current = job;
    notesChainRef.current = job.finally(() => {
      if (notesFlightRef.current === job) notesFlightRef.current = null;
    });
    return notesChainRef.current;
  }, []);

  const settleNotesSave = async () => {
    window.clearTimeout(notesTimerRef.current);
    try {
      await notesChainRef.current;
    } catch {
      // persistNotes reports its own failure.
    }
  };

  const onAcceptGenerated = async () => {
    await settleNotesSave();
    setSaveNote('');
    const notes = notesFromScaffold();
    const card = await run(() => styleCardsApi.saveInstruction(storyId, {
      name: scaffold.name || selected?.name || pendingGenerate?.name || UNTITLED_VOICE,
      resolved_text: resolvedEdit,
      direction_note: scaffold.direction_note,
      character_entity_id: scaffold.character_entity_id || selected?.character_entity_id || null,
      card_id: notesCardIdRef.current || selectedId || pendingGenerate?.id || null,
      ...notes,
    }));
    if (card) {
      notesCardIdRef.current = card.id;
      rememberNotes({ ...notes, direction_note: scaffold.direction_note || '' });
      setSavedScaffold(notes);
      setSelectedId(card.id);
      setPendingGenerate(null);
      await load(card.id);
      await refreshEffective();
      setSaveNote(VOICE_SAVED_FOR_BOOK);
    }
  };

  const onDraft = async () => {
    await settleNotesSave();
    const snapshot = {
      exemplars: scaffold.exemplars,
      mechanics: scaffold.mechanics,
      lexicon: scaffold.lexicon,
      tone: scaffold.tone,
    };
    const card = await run(() => styleCardsApi.draft(storyId, {
      name: scaffold.name || UNTITLED_VOICE,
      exemplars: snapshot.exemplars,
      mechanics: snapshot.mechanics,
      lexicon: snapshot.lexicon,
      tone: snapshot.tone,
      direction_note: scaffold.direction_note,
      character_entity_id: scaffold.character_entity_id || null,
      card_id: notesCardIdRef.current || selectedId || null,
    }));
    if (card) {
      notesCardIdRef.current = card.id;
      rememberNotes({ ...snapshot, direction_note: scaffold.direction_note || '' });
      setSavedScaffold(snapshot);
      setSelectedId(card.id);
      await load(card.id);
    }
  };

  const onSaveText = async () => {
    await settleNotesSave();
    setSaveNote('');
    const notes = notesFromScaffold();
    const card = await run(() => styleCardsApi.saveInstruction(storyId, {
      name: scaffold.name || selected?.name || UNTITLED_VOICE,
      resolved_text: resolvedEdit,
      direction_note: scaffold.direction_note,
      character_entity_id: scaffold.character_entity_id || null,
      card_id: notesCardIdRef.current || selectedId || null,
      ...notes,
    }));
    if (card) {
      notesCardIdRef.current = card.id;
      rememberNotes({ ...notes, direction_note: scaffold.direction_note || '' });
      setSavedScaffold(notes);
      setSelectedId(card.id);
      setPendingGenerate(null);
      await load(card.id);
      await refreshEffective();
      setSaveNote('Instruction saved');
    }
  };

  const onReject = async () => {
    const rejectId = selectedId || pendingGenerate?.id;
    const clearToEmpty = Boolean(pendingGenerate);
    if (!rejectId) {
      abandonNotesFlight();
      liveNotesRef.current = emptyNotes();
      savedNotesRef.current = emptyNotes();
      pendingEchoRef.current = null;
      notesCardIdRef.current = null;
      setPendingGenerate(null);
      setSelectedId(null);
      setResolvedEdit('');
      setSaveNote('');
      setScaffold(emptyScaffold());
      return;
    }
    const card = await run(() => styleCardsApi.reject(storyId, rejectId));
    if (card) {
      abandonNotesFlight();
      liveNotesRef.current = emptyNotes();
      savedNotesRef.current = emptyNotes();
      pendingEchoRef.current = null;
      notesCardIdRef.current = null;
      setSelectedId(null);
      setPendingGenerate(null);
      setResolvedEdit('');
      setSaveNote('');
      setScaffold(emptyScaffold());
      setPayload((prev) => (prev ? {
        ...prev,
        cards: (prev.cards || []).filter((c) => c.id !== rejectId),
      } : prev));
      setEffective((prev) => {
        if (!prev || prev.card?.id !== rejectId) return prev;
        return { ...prev, card: null, card_source: null };
      });
      await load(clearToEmpty ? null : undefined);
      await refreshEffective();
    }
  };

  const onSaveLibrary = async () => {
    if (!selectedId) return;
    const card = await run(() => styleCardsApi.saveToLibrary(storyId, selectedId));
    if (card) {
      setSelectedId(card.id);
      await load(card.id);
      await refreshEffective();
    }
  };

  const onPromoteBook = async () => {
    if (!selectedId) return;
    const card = await run(() => styleCardsApi.promote(storyId, selectedId, { target: 'book' }));
    if (card) {
      setSelectedId(card.id);
      await load(card.id);
      await refreshEffective();
    }
  };

  const onPromoteChapter = async () => {
    if (!selectedId || !chapterId) return;
    const card = await run(() => styleCardsApi.promote(storyId, selectedId, {
      target: 'chapter',
      sectionId: chapterId,
    }));
    if (card) {
      await load();
      await refreshEffective();
    }
  };

  const onSetScenePerson = async (mode) => {
    if (!sceneId) return;
    const next = await run(() => styleCardsApi.setSectionStyle(
      storyId,
      sceneId,
      sectionPersonBody(mode),
    ));
    if (!next) return;
    setOutline((prev) => {
      const apply = (list) => (list || []).map((item) => (
        String(item.id) === String(sceneId)
          ? { ...item, pov_mode: next.pov_mode || null }
          : item
      ));
      return {
        ...prev,
        scenes: apply(prev?.scenes),
        sections: apply(prev?.sections),
      };
    });
    await refreshEffective();
  };

  const onPointScene = async () => {
    if (!selectedId || !sceneId) return;
    setPointNote('');
    const next = await run(() => styleCardsApi.setSectionStyle(storyId, sceneId, {
      style_card_id: selectedId,
    }));
    if (!next) return;
    // Write keeps the compose it already loaded. Tell that page the pointer moved.
    notifySceneVoicePointed(storyId, sceneId);
    setPointNote(SAVED);
    await refreshEffective();
  };

  const onCopyScene = async () => {
    if (!sceneId) return;
    const card = await run(() => styleCardsApi.copyForScene(storyId, {
      sceneId,
      sourceCardId: selectedId || undefined,
    }));
    if (card) {
      revertDraftNotes();
      setSelectedId(card.id);
      await load();
      await refreshEffective();
    }
  };

  const onSavePov = async (args) => {
    const next = await run(() => styleCardsApi.setBookPov(storyId, args));
    if (next) await load();
  };

  const chapters = outlineChapters(outline);
  const scenes = outlineScenes(outline);
  const selectedScene = scenes.find((scene) => scene.id === sceneId) || null;
  const scenePerson = sceneId ? {
    scene_mode: selectedScene?.pov_mode || null,
    mode: selectedScene?.pov_mode || effective?.person?.mode || null,
    source: selectedScene?.pov_mode ? 'scene' : (effective?.person?.source || null),
  } : null;
  const povPeople = peopleForPov(outline);
  const voicePeople = peopleForVoice(outline);
  const cards = payload?.cards || [];
  const library = cards.filter((c) => c.in_library || c.is_book_default);

  const instructionDirty = (selected?.id ?? null) === appliedVoiceId && (
    scaffold.name !== (selected?.name || '')
    || resolvedEdit !== (selected?.resolved_text || '')
    || (scaffold.direction_note || '') !== (selected?.direction_note || '')
    || (scaffold.character_entity_id || '') !== (selected?.character_entity_id || '')
  );
  const scaffoldDirty = scaffold.exemplars !== savedScaffold.exemplars
    || scaffold.mechanics !== savedScaffold.mechanics
    || scaffold.lexicon !== savedScaffold.lexicon
    || scaffold.tone !== savedScaffold.tone;
  const guidanceDirty = scaffoldDirty
    || (scaffold.direction_note || '') !== (selected?.direction_note || '');
  const formDirty = instructionDirty || scaffoldDirty || povDirty;
  formDirtyRef.current = formDirty;
  guidanceRef.current = { storyId };
  // The style room replaces this form and brings its own leave dialog.
  useUnsavedChangesWarning(styleRoomEnabled() ? false : formDirty);

  useEffect(() => {
    if (!guidanceDirty || !storyId) return undefined;
    notesTimerRef.current = window.setTimeout(() => { void persistNotes(); }, NOTES_AUTOSAVE_MS);
    return () => window.clearTimeout(notesTimerRef.current);
  }, [
    guidanceDirty,
    storyId,
    scaffold.exemplars,
    scaffold.mechanics,
    scaffold.lexicon,
    scaffold.tone,
    scaffold.direction_note,
    persistNotes,
  ]);

  const editGuidance = (key) => (event) => {
    const value = event.target.value;
    liveNotesRef.current = { ...liveNotesRef.current, [key]: value };
    setNotesSaved(false);
    setScaffold((current) => ({ ...current, [key]: value }));
  };

  const onGuidanceBlur = (event) => {
    const key = NOTE_FIELD_IDS[event?.target?.id];
    if (key) {
      const value = event.target.value;
      liveNotesRef.current = { ...liveNotesRef.current, [key]: value };
      setScaffold((current) => (
        current[key] === value ? current : { ...current, [key]: value }
      ));
    }
    window.clearTimeout(notesTimerRef.current);
    void persistNotes();
  };

  const guardVoiceSwitch = (commit) => {
    if (voiceSwitchRef.current) return;
    if (!formDirtyRef.current) {
      commit();
      return;
    }
    voiceSwitchRef.current = commit;
    setVoiceSwitchOpen(true);
  };

  const keepVoiceEditing = () => {
    voiceSwitchRef.current = null;
    setVoiceSwitchOpen(false);
  };

  const discardVoiceTyping = () => {
    const commit = voiceSwitchRef.current;
    voiceSwitchRef.current = null;
    setVoiceSwitchOpen(false);
    commit?.();
  };

  const applySavedVoice = (id) => {
    abandonNotesFlight();
    const card = (payload?.cards || []).find((c) => c.id === id) || null;
    const notes = notesFromCard(card);
    liveNotesRef.current = notes;
    savedNotesRef.current = notes;
    pendingEchoRef.current = null;
    setPovDirty(false);
    setPovEpoch((epoch) => epoch + 1);
    setResolvedEdit(card?.resolved_text || '');
    setScaffold({
      name: card?.name || '',
      character_entity_id: card?.character_entity_id || '',
      ...notes,
    });
    setSavedScaffold({
      exemplars: notes.exemplars,
      mechanics: notes.mechanics,
      lexicon: notes.lexicon,
      tone: notes.tone,
    });
    setSelectedId(id);
  };

  const selectVoice = (id) => {
    if (id === selectedId) return;
    guardVoiceSwitch(() => applySavedVoice(id));
  };

  const handlePick = useCallback((project, story) => {
    setProjectAndStory(project, story);
    setPickerOpen(false);
    navigate(styleCardsPath(story.id));
  }, [setProjectAndStory, navigate]);

  if (pickerOpen) {
    return (
      <StoryPicker
        isOpen
        title={OPEN_BOOK_FOR_STYLE_NOTES}
        scopedProject={currentProject}
        onClose={() => setPickerOpen(false)}
        onPick={handlePick}
      />
    );
  }

  if (!storyId) {
    return (
      <div className="sc-page">
        <p className="sc-empty">{SELECT_BOOK_FOR_STYLE_NOTE}</p>
      </div>
    );
  }

  return (
    <div className="sc-page">
      {paidDialog}
      <header className="sc-header">
        <h1>{STYLE_NOTES}</h1>
        <p className="sc-header__lede">{STYLE_LEDE}</p>
      </header>

      {error && (
        <div className="sc-error" role="alert">
          <p>{error}</p>
          <button type="button" className="sc-btn" onClick={() => load()}>{TRY_AGAIN}</button>
        </div>
      )}
      {loading && <p className="sc-empty">{LOADING_STYLE_NOTES}</p>}

      <section className="sc-panel" aria-labelledby="sc-pov-heading">
        <h2 id="sc-pov-heading">POV — book default</h2>
        <p className="sc-source">
          {effective?.pov?.source
            ? (
              <>
                Inheritance source:{' '}
                {povSourceLabel(effective.pov.source)}
                {effective?.pov?.entity_name ? ` · ${effective.pov.entity_name}` : ''}
                {effective?.pov?.mode_label ? ` · ${effective.pov.mode_label}` : ''}
              </>
            )
            : NO_DEFAULT_VOICE_YET}
        </p>
        <NarrativePovForm
          key={povEpoch}
          value={payload?.book_pov}
          people={povPeople}
          busy={busy}
          idPrefix="sc-pov"
          onSave={onSavePov}
          onDirtyChange={onPovDirty}
        />
      </section>

      <section className="sc-panel" aria-labelledby="sc-from-card-heading">
        <h2 id="sc-from-card-heading">{GENERATE_VOICE_FROM_CHARACTER_NOTE}</h2>
        <p className="sc-hint">{GENERATE_VOICE_HINT}</p>
        <div className="sc-row">
          <div className="sc-field">
            <label htmlFor="sc-from-card-who">Character note</label>
            <select
              id="sc-from-card-who"
              value={voicePersonId}
              onChange={(e) => setVoicePersonId(e.target.value)}
            >
              <option value="">Pick a person</option>
              {voicePeople.map((p) => (
                <option key={p.pickId} value={p.pickId}>{p.name}</option>
              ))}
            </select>
          </div>
        </div>
        {voicePeople.length === 0 && (
          <p className="sc-empty">
            No confirmed Character notes on this book yet.
          </p>
        )}
        <div className="sc-actions">
          <button
            type="button"
            className="sc-btn sc-btn--primary"
            disabled={busy || !voicePersonId}
            onClick={() => guardVoiceSwitch(onGenerateFromCard)}
          >
            {GENERATE_VOICE_FROM_THEIR_CHARACTER_NOTE}
          </button>
        </div>
        {pendingGenerate && (
          <p className="sc-banner" role="status">
            {pendingGenerate.thin ? THIN_CHARACTER_NOTE : REVIEW_THIS_VOICE}
          </p>
        )}
      </section>

      <section className="sc-panel" aria-labelledby="sc-card-heading">
        <h2 id="sc-card-heading">What Write will use</h2>
        <p className="sc-hint">This is the instruction Write follows.</p>
        {effective?.started_from_parent && (
          <p className="sc-banner">
            {PARENT_VOICE_LINE}
            {effective.started_from_card?.name ? ` (${effective.started_from_card.name})` : ''},
            now this scene&apos;s.
          </p>
        )}
        <p className="sc-source">
          {selected
            ? `From the book: ${selected.name || statusLabel(selected) || 'Book default'}`
            : 'No note yet'}
        </p>
        <div className="sc-field sc-field--wide">
          <label htmlFor="sc-name">{VOICE_NAME}</label>
          <input
            id="sc-name"
            value={scaffold.name}
            onChange={(e) => {
              setSaveNote('');
              setScaffold((s) => ({ ...s, name: e.target.value }));
            }}
          />
        </div>
        <div className="sc-field sc-field--wide">
          <label htmlFor="sc-resolved">The one instruction</label>
          <textarea
            id="sc-resolved"
            value={resolvedEdit}
            onChange={(e) => {
              setSaveNote('');
              setResolvedEdit(e.target.value);
            }}
            placeholder={VOICE_INSTRUCTION_PLACEHOLDER}
          />
        </div>
        <div className="sc-actions">
          {pendingGenerate && (
            <button
              type="button"
              className="sc-btn sc-btn--primary"
              disabled={busy || !resolvedEdit.trim()}
              onClick={onAcceptGenerated}
            >
              Accept
            </button>
          )}
          <button type="button" className="sc-btn" disabled={busy || !resolvedEdit.trim()} onClick={onSaveText}>
            Save instruction
          </button>
          <button type="button" className="sc-btn sc-btn--danger" disabled={busy || !(selectedId || pendingGenerate)} onClick={onReject}>
            Reject
          </button>
          <button type="button" className="sc-btn" disabled={busy || !selectedId} onClick={onSaveLibrary}>
            Save to library
          </button>
          <button type="button" className="sc-btn sc-btn--primary" disabled={busy || !selectedId} onClick={onPromoteBook}>
            Use for the whole book
          </button>
        </div>
        {saveNote && (
          <p className="sc-saved" role="status" aria-live="polite">{saveNote}</p>
        )}
      </section>

      <section className="sc-panel" aria-labelledby="sc-draft-heading">
        <h2 id="sc-draft-heading">Extra guidance for Write</h2>
        <p className="sc-hint">{EXTRA_GUIDANCE_HINT}</p>
        <div className="sc-field sc-field--wide">
          <label htmlFor="sc-exemplars">Sample lines</label>
          <textarea
            id="sc-exemplars"
            value={scaffold.exemplars}
            onChange={editGuidance('exemplars')}
            onBlur={onGuidanceBlur}
          />
        </div>
        <div className="sc-field sc-field--wide">
          <label htmlFor="sc-mechanics">How sentences work</label>
          <textarea
            id="sc-mechanics"
            value={scaffold.mechanics}
            onChange={editGuidance('mechanics')}
            onBlur={onGuidanceBlur}
          />
        </div>
        <div className="sc-field sc-field--wide">
          <label htmlFor="sc-lexicon">Words we use</label>
          <textarea
            id="sc-lexicon"
            value={scaffold.lexicon}
            onChange={editGuidance('lexicon')}
            onBlur={onGuidanceBlur}
          />
        </div>
        <div className="sc-field sc-field--wide">
          <label htmlFor="sc-tone">Tone</label>
          <textarea
            id="sc-tone"
            value={scaffold.tone}
            onChange={editGuidance('tone')}
            onBlur={onGuidanceBlur}
          />
        </div>
        <div className="sc-field sc-field--wide">
          <label htmlFor="sc-direction">Where this is going</label>
          <textarea
            id="sc-direction"
            value={scaffold.direction_note}
            onChange={editGuidance('direction_note')}
            onBlur={onGuidanceBlur}
          />
        </div>
        {notesSaved && (
          <p className="sc-saved" role="status" aria-live="polite">{SAVED}</p>
        )}
        <div className="sc-field">
          <label htmlFor="sc-character">{VOICE_TIED_TO_PERSON}</label>
          <select
            id="sc-character"
            value={scaffold.character_entity_id}
            onChange={(e) => setScaffold((s) => ({ ...s, character_entity_id: e.target.value }))}
          >
            <option value="">{NONE_BOOK_VOICE}</option>
            {povPeople.map((c) => (
              <option key={c.pickId} value={c.pickId}>{c.name}</option>
            ))}
          </select>
        </div>
        <div className="sc-actions">
          <button type="button" className="sc-btn sc-btn--primary" disabled={busy} onClick={onDraft}>
            Draft
          </button>
        </div>
      </section>

      <section className="sc-panel" aria-labelledby="sc-cards-heading">
        <h2 id="sc-cards-heading">{SAVED_VOICES}</h2>
        {cards.length === 0 ? (
          <p className="sc-empty">{NO_VOICE_SAVED_YET}</p>
        ) : (
          <ul className="sc-library">
            {cards.map((card) => (
              <li key={card.id}>
                <button
                  type="button"
                  className={`sc-library__item${card.id === selectedId ? ' sc-library__item--active' : ''}`}
                  onClick={() => selectVoice(card.id)}
                >
                  <span className="sc-library__name">{card.name}</span>
                  <span className="sc-library__meta">{statusLabel(card)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="sc-panel" aria-labelledby="sc-lib-heading">
        <h2 id="sc-lib-heading">Library</h2>
        {library.length === 0 ? (
          <p className="sc-empty">{NO_VOICES_IN_LIBRARY}</p>
        ) : (
          <ul className="sc-library">
            {library.map((card) => (
              <li key={card.id}>
                <button
                  type="button"
                  className={`sc-library__item${card.id === selectedId ? ' sc-library__item--active' : ''}`}
                  onClick={() => selectVoice(card.id)}
                >
                  <span className="sc-library__name">{card.name}</span>
                  <span className="sc-library__meta">{statusLabel(card)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="sc-panel" aria-labelledby="sc-pointer-heading">
        <h2 id="sc-pointer-heading">Chapter override and scene pointer</h2>
        <p className="sc-hint">{POINT_CHAPTER_HINT}</p>
        <div className="sc-row">
          <div className="sc-field">
            <label htmlFor="sc-chapter">Chapter</label>
            <select id="sc-chapter" value={chapterId} onChange={(e) => setChapterId(e.target.value)}>
              <option value="">None</option>
              {chapters.map((c) => (
                <option key={c.id} value={c.id}>{c.display_name || 'Untitled chapter'}</option>
              ))}
            </select>
          </div>
          <div className="sc-field">
            <label htmlFor="sc-scene">Scene</label>
            <select id="sc-scene" value={sceneId} onChange={(e) => { setPointNote(''); setSceneId(e.target.value); }}>
              <option value="">None</option>
              {scenes.map((s) => (
                <option key={s.id} value={s.id}>{s.display_name || 'Untitled scene'}</option>
              ))}
            </select>
          </div>
        </div>
        {sceneId && (
          <PersonControl
            id="sc-scene-person"
            label={PERSON_FOR_SCENE}
            person={scenePerson}
            disabled={busy}
            onChange={onSetScenePerson}
          />
        )}
        <div className="sc-actions">
          <button type="button" className="sc-btn" disabled={busy || !selectedId || !chapterId} onClick={onPromoteChapter}>
            Use for this chapter
          </button>
          <button type="button" className="sc-btn" disabled={busy || !selectedId || !sceneId} onClick={onPointScene}>
            {POINT_SCENE_AT_VOICE}
          </button>
          <button type="button" className="sc-btn" disabled={busy || !sceneId} onClick={() => guardVoiceSwitch(onCopyScene)}>
            {COPY_BOOK_VOICE_INTO_SCENE}
          </button>
        </div>
        {pointNote && (
          <p className="sc-saved" role="status" aria-live="polite">{pointNote}</p>
        )}
      </section>
      <ConfirmModal
        isOpen={voiceSwitchOpen}
        title={UNSAVED_CHANGES_WARNING}
        message={UNSAVED_CLOSE_CHOICE}
        confirmLabel={DISCARD_UNSAVED}
        cancelLabel={KEEP_EDITING}
        destructive
        onConfirm={discardVoiceTyping}
        onCancel={keepVoiceEditing}
      />
    </div>
  );
}

export default function StyleCardsPage() {
  if (styleRoomEnabled()) return <StyleRoom />;
  return <StyleNotesPage />;
}
